import React, { PureComponent } from "react";
import { Card, Row, Col, Spin, Radio, Button } from "antd";
import { useCubeQuery } from "@cubejs-client/react";
import { withRouter } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";

const REPORT_CONFIG = {
  pending: {
    title: "Pending Orders to Work",
    cube: "PendingWorkOrder",
    countMeasure: "PendingWorkOrder.pending_count",
    totalMeasure: "PendingWorkOrder.pending_order_total",
    statusDimension: "PendingWorkOrder.wo_status",
    viewByOptions: {
      bay: "PendingWorkOrder.scheduled_bay",
      customer: "CBpartner.name",
      product: "Product.name",
    },
    color: "#2563eb",
  },
  delayed: {
    title: "Delayed > 48 Hours",
    cube: "DelayedSOrder",
    countMeasure: "DelayedSOrder.delayed_gt_48_count",
    totalMeasure: "DelayedSOrder.delayed_gt_48_total",
    statusDimension: "DelayedSOrder.wo_status",
    viewByOptions: {
      bay: "DelayedSOrder.scheduled_bay",
      customer: "CBpartner.name",
      product: "Product.name",
    },
    color: "#dc2626",
  },
  missingpo: {
    title: "Missing POs",
    cube: "MissedSOrder",
    countMeasure: "MissedSOrder.missing_po_count",
    totalMeasure: "MissedSOrder.missing_po_total",
    statusDimension: "MissedSOrder.wo_status",
    viewByOptions: {
      customer: "MissedSOrder.bpname",
      product: "Product.name",
    },
    color: "#ea580c",
  },
  pendingbill: {
    title: "Pending to Bill",
    cube: "PendingWorkOrderBill",
    countMeasure: "PendingWorkOrderBill.pending_count",
    totalMeasure: "PendingWorkOrderBill.pending_order_total",
    statusDimension: "PendingWorkOrderBill.agediff",
    viewByOptions: {
      customer: "CBpartner.name",
      age: "PendingWorkOrderBill.agediff",
      product: "Product.name",
    },
    color: "#8b5cf6",
  },
};

const STATUS_COLORS = {
  Open: "#3b82f6",
  Scheduled: "#f59e0b",
  "In Process": "#10b981",
  Stop: "#ef4444",
  Completed: "#6366f1",
  Closed: "#6b7280",
  null: "#9ca3af",
  "<2": "#22c55e",
  "2-6": "#eab308",
  "7-14": "#f97316",
  "15-31": "#ef4444",
  ">31": "#991b1b",
};

const formatCurrency = (val) => {
  if (val === null || val === undefined || isNaN(val)) return "$0";
  return "$" + Number(val).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

const formatNumber = (val) => {
  if (val === null || val === undefined || isNaN(val)) return "0";
  return Number(val).toLocaleString("en-US");
};

// Category card with mini bar chart
const CategoryCard = ({ name, count, total, statusData, config, history, facility, customer, product, viewBy }) => {
  const chartData = statusData.map((s) => ({
    name: s.status || "N/A",
    count: s.count,
  }));

  return (
    <Card
      style={{ marginBottom: 12, cursor: "pointer", borderLeft: `3px solid ${config.color}` }}
      hoverable
      onClick={() => {
        const params = new URLSearchParams();
        params.set("report", new URLSearchParams(window.location.hash.split("?")[1] || "").get("report") || "pending");
        if (facility) params.set("facility", facility);
        if (customer) params.set("customer", customer);
        if (product) params.set("product", product);
        params.set("viewBy", viewBy);
        params.set("category", name);
        history.push(`/needsaction/detail?${params.toString()}`);
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>{name || "N/A"}</div>
          <div style={{ display: "flex", gap: 16, fontSize: 13, color: "#666" }}>
            <span><strong style={{ fontSize: 20, color: config.color }}>{formatNumber(count)}</strong> orders</span>
            <span>{formatCurrency(total)}</span>
          </div>
        </div>
        {chartData.length > 0 && (
          <div style={{ width: 200, height: 50 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} layout="vertical" margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="name" hide />
                <Tooltip />
                <Bar dataKey="count" stackId="a">
                  {chartData.map((entry, i) => (
                    <Cell key={i} fill={STATUS_COLORS[entry.name] || "#9ca3af"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </Card>
  );
};

// Main category view
const CategoryView = ({ config, facility, customer, product, viewBy, history }) => {
  const viewByDimension = config.viewByOptions[viewBy] || Object.values(config.viewByOptions)[0];
  const filters = [];
  if (facility) filters.push({ dimension: "AdOrg.facility", operator: "equals", values: [facility] });
  if (customer) filters.push({ dimension: "CBpartner.name", operator: "equals", values: [customer] });
  if (product) filters.push({ dimension: "Product.name", operator: "equals", values: [product] });
  const facilityFilter = filters;

  // Query grouped by category
  const { resultSet: mainResult, isLoading: mainLoading } = useCubeQuery({
    measures: [config.countMeasure, config.totalMeasure],
    dimensions: [viewByDimension],
    filters: facilityFilter,
    order: { [config.totalMeasure]: "desc" },
  });

  // Query grouped by category + status
  const { resultSet: statusResult, isLoading: statusLoading } = useCubeQuery({
    measures: [config.countMeasure],
    dimensions: [viewByDimension, config.statusDimension],
    filters: facilityFilter,
  });

  if (mainLoading || statusLoading) return <Spin style={{ display: "block", margin: "40px auto" }} />;
  if (!mainResult) return <div style={{ color: "#999", padding: 20 }}>No data available</div>;

  const mainRows = mainResult.tablePivot();
  const statusRows = statusResult ? statusResult.tablePivot() : [];

  // Build status data map
  const statusMap = {};
  statusRows.forEach((row) => {
    const cat = row[viewByDimension] || "N/A";
    if (!statusMap[cat]) statusMap[cat] = [];
    statusMap[cat].push({
      status: row[config.statusDimension],
      count: Number(row[config.countMeasure]) || 0,
    });
  });

  return (
    <div>
      {mainRows.map((row, i) => {
        const name = row[viewByDimension] || "N/A";
        return (
          <CategoryCard
            key={i}
            name={name}
            count={Number(row[config.countMeasure]) || 0}
            total={Number(row[config.totalMeasure]) || 0}
            statusData={statusMap[name] || []}
            config={config}
            history={history}
            facility={facility}
            customer={customer}
            product={product}
            viewBy={viewBy}
          />
        );
      })}
      {mainRows.length === 0 && <div style={{ color: "#999", padding: 20 }}>No data for this view</div>}
    </div>
  );
};

class NeedsActionCategory extends PureComponent {
  constructor(props) {
    super(props);
    const params = new URLSearchParams(props.location.search);
    const report = params.get("report") || "pending";
    const facility = params.get("facility") || "";
    const customer = params.get("customer") || "";
    const product = params.get("product") || "";
    const config = REPORT_CONFIG[report] || REPORT_CONFIG.pending;
    const defaultViewBy = Object.keys(config.viewByOptions)[0];

    this.state = {
      report,
      facility,
      customer,
      product,
      viewBy: defaultViewBy,
    };
  }

  render() {
    const { report, facility, customer, product, viewBy } = this.state;
    const { history } = this.props;
    const config = REPORT_CONFIG[report] || REPORT_CONFIG.pending;
    const facilityName = facility
      ? `Facility ${facility}`
      : "All Facilities";

    return (
      <div style={{ padding: 16, background: "#f5f5f5", minHeight: "100vh" }}>
        {/* Header */}
        <div style={{ marginBottom: 16 }}>
          <Button type="link" onClick={() => history.push("/needsaction")} style={{ padding: 0, marginBottom: 8 }}>
            &larr; Back to Dashboard
          </Button>
          <h2 style={{ margin: 0, fontSize: 20 }}>
            {config.title} - <span style={{ color: "#666", fontWeight: "normal" }}>{facilityName}</span>
          </h2>
        </div>

        {/* View By Toggle */}
        <div style={{ marginBottom: 16 }}>
          <span style={{ marginRight: 8, color: "#666" }}>View By:</span>
          <Radio.Group
            value={viewBy}
            onChange={(e) => this.setState({ viewBy: e.target.value })}
            buttonStyle="solid"
            size="small"
          >
            {Object.keys(config.viewByOptions).map((key) => (
              <Radio.Button key={key} value={key}>
                {key.charAt(0).toUpperCase() + key.slice(1)}
              </Radio.Button>
            ))}
          </Radio.Group>
        </div>

        {/* Category Cards */}
        <CategoryView config={config} facility={facility} customer={customer} product={product} viewBy={viewBy} history={history} />
      </div>
    );
  }
}

export default withRouter(NeedsActionCategory);
